// FAQ.js
import React, { useState } from "react";
import { ChevronDown, ChevronUp, Phone } from "lucide-react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What services does Allawah Lodge provide?",
      answer:
        "We offer a comprehensive range of in-home care services including Alzheimer's and Dementia Care, Companionship Care, Hospice Care, Personal Care, Post-Operative Care, Respite Care, Specialty Care, and Spinal Cord Injury Care. Each service is tailored to meet the unique needs of our clients and their families.",
    },
    {
      question: "How do I get started with care services?",
      answer:
        "Getting started is simple! Call our office at (02) 6927 2706 to speak with our friendly team. We'll schedule a complimentary in-home assessment to understand your specific needs and create a customised care plan. From there, we'll match you with a qualified caregiver who fits your requirements and preferences.",
    },
    {
      question: "Are your caregivers qualified and insured?",
      answer:
        "Absolutely. All our caregivers are thoroughly trained, certified, and insured. They undergo comprehensive background checks and receive ongoing training to ensure they provide the highest quality of care. Our team is equipped to handle a wide range of care needs with professionalism and compassion.",
    },
    {
      question: "What areas do you serve?",
      answer:
        "We proudly serve Coolamon, Wagga Wagga, and surrounding areas throughout the Riverina region. Our goal is to bring quality in-home care to families across our community, ensuring seniors can age comfortably and safely in their own homes.",
    },
    {
      question: "Can I choose my caregiver?",
      answer:
        "Yes! We believe the relationship between caregiver and client is crucial to quality care. During your assessment, we'll discuss your preferences and match you with a caregiver whose personality, skills, and experience align with your needs. If for any reason the match isn't perfect, we'll work with you to find a better fit.",
    },
    {
      question: "What is the cost of care services?",
      answer:
        "Care costs vary depending on the type and frequency of services needed. During your complimentary in-home assessment, we'll provide a transparent breakdown of costs based on your customised care plan. We're committed to working with families to find affordable solutions that meet their needs.",
    },
    {
      question: "Do you offer 24/7 care?",
      answer:
        "Yes, we offer flexible care schedules ranging from a few hours per week to around-the-clock care, depending on your needs. Whether you need daily assistance, overnight care, or live-in support, we can create a schedule that works for you and your family.",
    },
    {
      question: "What is respite care and how does it work?",
      answer:
        "Respite care provides temporary relief for family caregivers who need a break to rest, recharge, or attend to personal matters. Our trained caregivers step in to provide the same quality care your loved one receives, whether it's for a few hours, a day, or several days. This allows you to take time for yourself knowing your loved one is in safe, compassionate hands.",
    },
    {
      question: "How do you ensure the safety of clients?",
      answer:
        "Safety is our top priority. Our caregivers are trained in fall prevention, emergency response, and proper transfer techniques. We conduct regular safety assessments of the home environment and maintain open communication with families and healthcare professionals. All our staff undergo rigorous background checks and are bonded and insured.",
    },
    {
      question: "Can you work with my loved one's existing healthcare team?",
      answer:
        "Absolutely! We believe in collaborative care and work closely with doctors, nurses, therapists, and other healthcare professionals involved in your loved one's care. Our caregivers can attend medical appointments, communicate important health updates, and ensure care plans are followed consistently.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-50 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-700 font-light mb-8">
            Find answers to common questions about our care services,
            caregivers, and how we can support you and your loved ones.
          </p>
          <a
            href="tel:+61269272706"
            className="inline-flex items-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors"
          >
            <Phone size={20} />
            CALL: (02) 6927 2706
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  {openIndex === index ? (
                    <ChevronUp
                      size={24}
                      className="text-[#E85D2C] flex-shrink-0"
                    />
                  ) : (
                    <ChevronDown
                      size={24}
                      className="text-gray-400 flex-shrink-0"
                    />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 font-light leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="bg-gray-50 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-700 font-light mb-8">
            Our friendly team is here to help. Give us a call or visit our
            contact page to get in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+61269272706"
              className="inline-flex items-center justify-center gap-2 bg-[#E85D2C] hover:bg-[#d54d1f] text-white font-medium px-6 py-3 rounded-full transition-colors"
            >
              <Phone size={20} />
              CALL: (02) 6927 2706
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-medium px-6 py-3 rounded-full border-2 border-gray-300 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
